import { ActionCreator, ReducerTypes, Store, createFeature, createReducer, on } from '@ngrx/store';

import { getUUID } from '@dmb/core';

import { logError } from '../../../helper/log.helper';
import { ExistingTrack, OverallMetadata, ProductData, TrackMetadata } from '../../../model';
import {
  DmbWizardValue,
  WizardFeatureState,
  WizardStep,
  defaultTrackValidation,
  initialState,
} from '../../../model/product/wizard';
import { ReleaseDateData } from '../release-date/release-date.model';
import {
  AsyncValidationActions,
  DeepActionCreator,
  UploadActions,
  WizardActions,
  WizardSessionActions,
  setValueActions,
} from './app.actions';
import { allContributors } from './contributor.reducer';
import { countriesToggled } from './countries.reducer';
import {
  neighbouringRightsCountriesToggled,
  neighbouringRightsEndDateUpdated,
  neighbouringRightsPercentagesUpdated,
  neighbouringRightsStartDateUpdated,
} from './neighboringRights.reducer';
import { allSetOrDisallowed, isAllowed, onlyIfAllowed, updateAllowed, updateUploadInfo } from './utils';

function makeNewValue(): DmbWizardValue {
  return { ...initialState.value, id: getUUID() };
}

function createSetValueActionHandler<BaseState, TargetState>(
  actions: DeepActionCreator<TargetState>,
  list: ReducerTypes<BaseState, ActionCreator[]>[],
  baseMod: (baseState: BaseState, mod: (tState: TargetState) => TargetState) => BaseState,
) {
  const handler = on(actions, (state: BaseState, { value }) => {
    return baseMod(state, () => value);
  });

  list.push(handler);

  Object.entries(actions).forEach(([key, value]) => {
    const nestedMod = (state: BaseState, updateNestedValue: (val: unknown) => unknown) => {
      return baseMod(state, (oldValue) => {
        const oldNestedValue = (oldValue as Record<string, unknown>)[key];
        const newNestedValue = updateNestedValue(oldNestedValue);
        return oldNestedValue === newNestedValue
          ? oldValue
          : {
              ...oldValue,
              [key]: newNestedValue,
            };
      });
    };

    createSetValueActionHandler(value, list, nestedMod);
  });
}

const setValueActionHandler: ReducerTypes<WizardFeatureState, ActionCreator[]>[] = [];

createSetValueActionHandler<WizardFeatureState, DmbWizardValue>(
  setValueActions,
  setValueActionHandler,
  (state, update) => {
    const wizardValue = state.value;
    const newWizardValue = update(wizardValue);
    return wizardValue === newWizardValue
      ? state
      : {
          ...state,
          value: newWizardValue,
        };
  },
);

const reducer = createReducer(
  initialState,
  ...setValueActionHandler,
  on(WizardSessionActions.newTimestamp, (state) => ({ ...state, value: { ...state.value, lastModified: Date.now() } })),
  on(
    WizardSessionActions.sessionSelected,
    (state): WizardFeatureState => ({
      ...state,
      dbLoading: true,
    }),
  ),
  on(
    WizardSessionActions.sessionLoaded,
    (state, { value }): WizardFeatureState => ({
      ...state,
      dbLoading: false,
      value: value,
      asyncValidations: initialState.asyncValidations,
    }),
  ),
  on(
    WizardSessionActions.sessionLoadFailed,
    (state): WizardFeatureState => ({
      ...state,
      dbLoading: false,
    }),
  ),
  on(
    WizardSessionActions.newSession,
    (state): WizardFeatureState => ({
      ...state,
      value: makeNewValue(),
    }),
  ),
  on(WizardSessionActions.deleteSessionStarted, (state): WizardFeatureState => ({ ...state, dbLoading: true })),
  on(WizardSessionActions.deleteSessionDone, (state, { id }): WizardFeatureState => {
    if (state.value.id === id) {
      return { ...state, dbLoading: false, value: makeNewValue() };
    }

    return { ...state, dbLoading: false };
  }),
  on(
    WizardActions.eanGenerated,
    (state, { ean }): WizardFeatureState => ({
      ...state,
      value: { ...state.value, eanWasGenerated: true, productData: { ...state.value.productData, eanUpc: ean } },
    }),
  ),
  on(
    WizardActions.appliedContributorsToTracks,
    (state, { trackIds }): WizardFeatureState => ({
      ...state,
      value: {
        ...state.value,
        tracklisting: state.value.tracklisting.map((track) =>
          track.existing || !trackIds.includes(track.id)
            ? track
            : {
                ...track,
                contributors: state.value.productContributors,
              },
        ),
      },
    }),
  ),
  on(WizardActions.copiedTrackContributors, (state, { sourceTrackId, targetTrackId }): WizardFeatureState => {
    const sourceTrack = state.value.tracklisting.find((t) => t.id === sourceTrackId);
    if (!sourceTrack) {
      return state;
    }

    if (sourceTrack.existing) {
      const existingData = state.existingTrackData[sourceTrack.id];
      if (typeof existingData === 'string') {
        return state;
      }

      const combinedContributors = state.value.contributors.slice();

      existingData.contributors.forEach(({ contributor }) => {
        if (combinedContributors.every((c) => c.id !== contributor.id)) {
          combinedContributors.push(contributor);
        }
      });

      return {
        ...state,
        value: {
          ...state.value,
          contributors: combinedContributors,
          tracklisting: state.value.tracklisting.map((track) =>
            track.existing || track.id !== targetTrackId
              ? track
              : {
                  ...track,
                  contributors: existingData.contributors,
                },
          ),
        },
      };
    } else {
      return {
        ...state,
        value: {
          ...state.value,
          tracklisting: state.value.tracklisting.map((track) =>
            track.existing || track.id !== targetTrackId
              ? track
              : {
                  ...track,
                  contributors: sourceTrack.contributors,
                },
          ),
        },
      };
    }
  }),
  on(WizardActions.productContributorsUpdated, (state, { newAssignments }): WizardFeatureState => {
    return {
      ...state,
      value: {
        ...state.value,
        productContributors: newAssignments,
        contributors: allContributors(state, newAssignments),
      },
    };
  }),
  on(WizardActions.trackContributorsUpdated, (state, { newAssignments, id }): WizardFeatureState => {
    return {
      ...state,
      value: {
        ...state.value,
        tracklisting: state.value.tracklisting.map((track) =>
          track.id !== id ? track : { ...track, contributors: newAssignments },
        ),
        contributors: allContributors(state, newAssignments),
      },
    };
  }),
  on(WizardActions.artistIdsSelected, (state, { id, artistId, store }) => {
    const storeKey = store === 'Spotify' ? 'spotifyArtistId' : 'appleArtistId';

    return {
      ...state,
      value: {
        ...state.value,
        contributors: state.value.contributors.map((c) => (c.id !== id ? c : { ...c, [storeKey]: artistId })),
      },
    };
  }),
  on(WizardActions.appliedMetadataToTracks, (state, { trackIds }): WizardFeatureState => {
    const { explicit, appleDigitalMastered, appleEmail, ...commonMetaData } = state.value.overallMetadata;

    return {
      ...state,
      value: {
        ...state.value,
        tracklisting: state.value.tracklisting.map((track) =>
          track.existing || !trackIds.includes(track.id)
            ? track
            : {
                ...track,
                metadata: { ...track.metadata, ...commonMetaData },
              },
        ),
      },
    };
  }),
  on(WizardActions.copiedTrackMetadata, (state, { sourceTrackId, targetTrackId }): WizardFeatureState => {
    const sourceTrack = state.value.tracklisting.find((t) => t.id === sourceTrackId);
    if (!sourceTrack) {
      return state;
    }

    let newMetadata: TrackMetadata;
    if (sourceTrack.existing) {
      const existingData = state.existingTrackData[sourceTrack.id];
      if (typeof existingData === 'string') {
        return state;
      }

      newMetadata = existingData.metadata;
    } else {
      newMetadata = sourceTrack.metadata;
    }

    return {
      ...state,
      value: {
        ...state.value,
        tracklisting: state.value.tracklisting.map((track) =>
          track.existing || track.id !== targetTrackId
            ? track
            : {
                ...track,
                metadata: newMetadata,
              },
        ),
      },
    };
  }),
  on(WizardActions.trackMetadataUpdated, (state, { id, newMetadata }): WizardFeatureState => {
    return {
      ...state,
      value: {
        ...state.value,
        tracklisting: state.value.tracklisting.map((track) =>
          track.id !== id || track.existing ? track : { ...track, metadata: { ...track.metadata, ...newMetadata } },
        ),
      },
    };
  }),
  on(WizardActions.trackDeleteStarted, (state, { id }): WizardFeatureState => {
    return {
      ...state,
      trackDeletePending: [...state.trackDeletePending, id],
    };
  }),
  on(WizardActions.trackDeleteDone, (state, { id }): WizardFeatureState => {
    return {
      ...state,
      trackDeletePending: state.trackDeletePending.filter((listId) => listId !== id),
    };
  }),
  on(WizardActions.countriesToggled, (state, { toggleList }): WizardFeatureState => {
    return countriesToggled(state, toggleList);
  }),
  on(setValueActions.productType, (state, { value }): WizardFeatureState => {
    const isSpokenWord = value === 'AUDIOBOOK' || value === 'RADIO_PLAY';
    let newProductData: ProductData = state.value.productData;
    let newOverallMetaData: OverallMetadata = state.value.overallMetadata;
    let newReleaseDates: ReleaseDateData = state.value.releaseDates;
    let newTracks = state.value.tracklisting;

    if (!isSpokenWord) {
      newProductData = {
        ...newProductData,
        genre: state.value.productData.genre,
        bisacGenre: [],
        wgsGenre: [],
        ageRating: '',
        version: '',
        reviewQuotes: '',
        keywords: '',
        seriesName: '',
        seriesNumber: '',
        digitalPrice: '',
      };

      newTracks = newTracks.map((t) => {
        if (t.existing) {
          return t;
        }

        return {
          ...t,
          metadata: {
            ...t.metadata,
            bisacGenre: [],
            wgsGenre: [],
          },
        };
      });

      newOverallMetaData = {
        ...newOverallMetaData,
        genre: state.value.overallMetadata.genre,
        bisacGenre: [],
        wgsGenre: [],
      };

      newReleaseDates = {
        ...newReleaseDates,
        price: '',
        isbn: '',
        spokenWordCopyrightReleaseDate: '',
        spokenWordCopyrightYear: '',
      };
    } else {
      newProductData = {
        ...newProductData,
        genre:
          state.value.productData.genre.length > 1 ? [state.value.productData.genre[0]] : state.value.productData.genre,
      };
      newOverallMetaData = {
        ...newOverallMetaData,
        genre:
          state.value.overallMetadata.genre.length > 1
            ? [state.value.overallMetadata.genre[0]]
            : state.value.overallMetadata.genre,
      };

      newTracks = newTracks.map((t) => {
        if (t.existing) {
          return t;
        }

        const newMetadata = t.metadata;
        return {
          ...t,
          metadata: {
            ...newMetadata,
            genre: newMetadata.genre.length > 1 ? [newMetadata.genre[0]] : newMetadata.genre,
          },
        };
      });
    }
    return {
      ...state,
      value: {
        ...state.value,
        productData: {
          ...newProductData,
        },
        overallMetadata: {
          ...newOverallMetaData,
        },
        releaseDates: {
          ...newReleaseDates,
        },
        tracklisting: newTracks,
      },
    };
  }),
  on(WizardActions.shopsChanged, (state, { newShops }): WizardFeatureState => {
    let noChanges = true;

    const newShopSelection = newShops.map(
      ({ id, preselect, downloadAllowed, streamingAllowed, ringtoneAllowed, specialAllowed, specialType }) => {
        const foundSelection = state.value.shopSelection.shopSelection.find((sel) => sel.id === id);

        if (!foundSelection) {
          // New Shop
          const initialState = preselect;
          noChanges = false;
          return {
            id,
            download: downloadAllowed ? initialState : ('disallowed' as const),
            streaming: streamingAllowed ? initialState : ('disallowed' as const),
            ringtone: ringtoneAllowed ? initialState : ('disallowed' as const),
            special: specialAllowed ? initialState : ('disallowed' as const),
            specialType,
          };
        }

        if (
          isAllowed(foundSelection.download) !== downloadAllowed ||
          isAllowed(foundSelection.streaming) !== streamingAllowed ||
          isAllowed(foundSelection.ringtone) !== ringtoneAllowed ||
          isAllowed(foundSelection.special) !== specialAllowed ||
          foundSelection.specialType !== specialType
        ) {
          noChanges = false;
          return {
            ...foundSelection,
            download: updateAllowed(downloadAllowed, foundSelection.download, preselect),
            streaming: updateAllowed(streamingAllowed, foundSelection.streaming, preselect),
            ringtone: updateAllowed(ringtoneAllowed, foundSelection.ringtone, preselect),
            special: updateAllowed(specialAllowed, foundSelection.special, preselect),
            specialType,
          };
        } else {
          return foundSelection;
        }
      },
    );

    if (noChanges) {
      const noShopRemoved = state.value.shopSelection.shopSelection.every((sel) =>
        newShops.some((shop) => shop.id === sel.id),
      );

      if (noShopRemoved) {
        return state;
      }
    }

    return {
      ...state,
      value: {
        ...state.value,
        shopSelection: {
          ...state.value.shopSelection,
          shopSelection: newShopSelection,
        },
      },
    };
  }),
  on(WizardActions.shopEntryToggled, (state, { id, entry }): WizardFeatureState => {
    return {
      ...state,
      value: {
        ...state.value,
        shopSelection: {
          ...state.value.shopSelection,
          shopSelection: state.value.shopSelection.shopSelection.map((s) =>
            id !== s.id ? s : { ...s, [entry]: onlyIfAllowed(s[entry], !s[entry]) },
          ),
        },
      },
    };
  }),
  on(WizardActions.shopRowToggled, (state, { id }): WizardFeatureState => {
    return {
      ...state,
      value: {
        ...state.value,
        shopSelection: {
          ...state.value.shopSelection,
          shopSelection: state.value.shopSelection.shopSelection.map((s) => {
            if (s.id !== id) {
              return s;
            }
            const allSelected = allSetOrDisallowed(s);

            return {
              ...s,
              download: onlyIfAllowed(s.download, !allSelected),
              streaming: onlyIfAllowed(s.streaming, !allSelected),
              ringtone: onlyIfAllowed(s.ringtone, !allSelected),
              special: onlyIfAllowed(s.special, !allSelected),
            };
          }),
        },
      },
    };
  }),
  on(WizardActions.shopColumnToggled, (state, { entry }): WizardFeatureState => {
    const allSelected = state.value.shopSelection.shopSelection.every((sel) => sel[entry]);

    return {
      ...state,
      value: {
        ...state.value,
        shopSelection: {
          ...state.value.shopSelection,
          shopSelection: state.value.shopSelection.shopSelection.map((s) => ({
            ...s,
            [entry]: onlyIfAllowed(s[entry], !allSelected),
          })),
        },
      },
    };
  }),
  on(WizardActions.shopAllToggled, (state): WizardFeatureState => {
    const allSelected = state.value.shopSelection.shopSelection.every(allSetOrDisallowed);

    return {
      ...state,
      value: {
        ...state.value,
        shopSelection: {
          ...state.value.shopSelection,
          shopSelection: state.value.shopSelection.shopSelection.map((s) => ({
            ...s,
            download: onlyIfAllowed(s.download, !allSelected),
            streaming: onlyIfAllowed(s.streaming, !allSelected),
            ringtone: onlyIfAllowed(s.ringtone, !allSelected),
            special: onlyIfAllowed(s.special, !allSelected),
          })),
        },
      },
    };
  }),
  on(WizardActions.neighbouringRightsCountriesToggled, (state, { toggleList }): WizardFeatureState => {
    return neighbouringRightsCountriesToggled(state, toggleList);
  }),
  on(WizardActions.neighbouringRightsPercentagesUpdated, (state, { newPercentage, updatedId }): WizardFeatureState => {
    return neighbouringRightsPercentagesUpdated(state, newPercentage, updatedId);
  }),
  on(WizardActions.neighbouringRightsStartDateUpdated, (state, { newDate, updatedId }): WizardFeatureState => {
    return neighbouringRightsStartDateUpdated(state, newDate, updatedId);
  }),
  on(WizardActions.neighbouringRightsEndDateUpdated, (state, { newDate, updatedId }): WizardFeatureState => {
    return neighbouringRightsEndDateUpdated(state, newDate, updatedId);
  }),
  on(WizardActions.templateApplied, (state, { patch }): WizardFeatureState => {
    return {
      ...state,
      value: {
        ...state.value,
        ...(patch.productType !== undefined && { productType: patch.productType }),
        ...(patch.productData && { productData: { ...state.value.productData, ...patch.productData } }),
        ...(patch.productContributors && {
          productContributors: patch.productContributors,
          contributors: allContributors(state, patch.productContributors),
        }),
        ...(patch.releaseDates && { releaseDates: { ...state.value.releaseDates, ...patch.releaseDates } }),
        ...(patch.overallMetadata && { overallMetadata: { ...state.value.overallMetadata, ...patch.overallMetadata } }),
        ...(patch.countrySelection && {
          countrySelection: { ...state.value.countrySelection, ...patch.countrySelection },
        }),
        ...(patch.shopSelection && { shopSelection: { ...state.value.shopSelection, ...patch.shopSelection } }),
        ...(patch.neighbouringRightsSelection && {
          neighbouringRightsSelection: {
            ...state.value.neighbouringRightsSelection,
            ...patch.neighbouringRightsSelection,
          },
        }),
        ...(patch.contractSelection && {
          contractSelection: { ...state.value.contractSelection, ...patch.contractSelection },
        }),
      },
    };
  }),
  on(AsyncValidationActions.startedValidatingProductcode, (state): WizardFeatureState => {
    return {
      ...state,
      asyncValidations: {
        ...state.asyncValidations,
        eanUpc: { ...state.asyncValidations.eanUpc, pending: true },
      },
    };
  }),
  on(AsyncValidationActions.finishedValidatingProductcode, (state, { result }): WizardFeatureState => {
    return {
      ...state,
      asyncValidations: {
        ...state.asyncValidations,
        eanUpc: { ...state.asyncValidations.eanUpc, errors: result, pending: false },
      },
    };
  }),
  on(AsyncValidationActions.startedValidatingTitle, (state, { validating, on, trackId }): WizardFeatureState => {
    if (on === 'ALBUM') {
      if (validating === 'TITLE') {
        return {
          ...state,
          asyncValidations: {
            ...state.asyncValidations,
            productTitle: { ...state.asyncValidations.productTitle, pending: true },
          },
        };
      }

      return {
        ...state,
        asyncValidations: {
          ...state.asyncValidations,
          productTitleVersion: { ...state.asyncValidations.productTitleVersion, pending: true },
        },
      };
    }

    const entry = state.asyncValidations.tracks[trackId] ?? defaultTrackValidation;

    if (validating === 'TITLE') {
      return {
        ...state,
        asyncValidations: {
          ...state.asyncValidations,
          tracks: {
            ...state.asyncValidations.tracks,
            [trackId]: {
              ...entry,
              title: { ...entry.title, pending: true },
            },
          },
        },
      };
    }

    return {
      ...state,
      asyncValidations: {
        ...state.asyncValidations,
        tracks: {
          ...state.asyncValidations.tracks,
          [trackId]: {
            ...entry,
            titleVersion: { ...entry.titleVersion, pending: true },
          },
        },
      },
    };
  }),
  on(
    AsyncValidationActions.finishedValidatingTitle,
    (state, { errors, warnings, explicit, on, trackId, validating }): WizardFeatureState => {
      const validation = { pending: false, errors, warnings, explicit };

      if (on === 'ALBUM') {
        if (validating === 'TITLE') {
          return {
            ...state,
            asyncValidations: {
              ...state.asyncValidations,
              productTitle: validation,
            },
          };
        }

        return {
          ...state,
          asyncValidations: {
            ...state.asyncValidations,
            productTitleVersion: validation,
          },
        };
      }

      const entry = state.asyncValidations.tracks[trackId] ?? defaultTrackValidation;

      if (validating === 'TITLE') {
        return {
          ...state,
          asyncValidations: {
            ...state.asyncValidations,
            tracks: {
              ...state.asyncValidations.tracks,
              [trackId]: { ...entry, title: validation },
            },
          },
        };
      }

      return {
        ...state,
        asyncValidations: {
          ...state.asyncValidations,
          tracks: {
            ...state.asyncValidations.tracks,
            [trackId]: { ...entry, titleVersion: validation },
          },
        },
      };
    },
  ),
  on(AsyncValidationActions.startedValidatingCovers, (state): WizardFeatureState => {
    return {
      ...state,
      asyncValidations: {
        ...state.asyncValidations,
        coverImages: { ...state.asyncValidations.coverImages, pending: true },
      },
    };
  }),
  on(AsyncValidationActions.finishedValidatingCovers, (state, { allErrors }): WizardFeatureState => {
    return {
      ...state,
      asyncValidations: {
        ...state.asyncValidations,
        coverImages: { pending: false, allErrors },
      },
    };
  }),
  on(AsyncValidationActions.startedValidatingTrack, (state, { trackId }): WizardFeatureState => {
    const entry = state.asyncValidations.tracks[trackId] ?? defaultTrackValidation;
    return {
      ...state,
      asyncValidations: {
        ...state.asyncValidations,
        tracks: {
          ...state.asyncValidations.tracks,
          [trackId]: { ...entry, general: { pending: true, errors: null } },
        },
      },
    };
  }),
  on(AsyncValidationActions.finishedValidatingTrack, (state, { trackId, errors }): WizardFeatureState => {
    const entry = state.asyncValidations.tracks[trackId] ?? defaultTrackValidation;
    return {
      ...state,
      asyncValidations: {
        ...state.asyncValidations,
        tracks: {
          ...state.asyncValidations.tracks,
          [trackId]: { ...entry, general: { pending: false, errors } },
        },
      },
    };
  }),
  on(AsyncValidationActions.startedValidatingTrackISRC, (state, { trackId }): WizardFeatureState => {
    const entry = state.asyncValidations.tracks[trackId] ?? defaultTrackValidation;
    return {
      ...state,
      asyncValidations: {
        ...state.asyncValidations,
        tracks: {
          ...state.asyncValidations.tracks,
          [trackId]: { ...entry, isrc: { pending: true, error: null } },
        },
      },
    };
  }),
  on(AsyncValidationActions.finishedValidatingTrackISRC, (state, { trackId, error }): WizardFeatureState => {
    const entry = state.asyncValidations.tracks[trackId] ?? defaultTrackValidation;
    return {
      ...state,
      asyncValidations: {
        ...state.asyncValidations,
        tracks: {
          ...state.asyncValidations.tracks,
          [trackId]: { ...entry, isrc: { pending: false, error } },
        },
      },
    };
  }),
  on(AsyncValidationActions.startedValidatingPreviewFile, (state): WizardFeatureState => {
    return {
      ...state,
      asyncValidations: {
        ...state.asyncValidations,
        tracksPreview: {
          ...state.asyncValidations.tracksPreview,
          pending: true,
        },
      },
    };
  }),
  on(AsyncValidationActions.finishedValidatingPreviewFile, (state, { errors }): WizardFeatureState => {
    return {
      ...state,
      asyncValidations: {
        ...state.asyncValidations,
        tracksPreview: {
          errors,
          pending: false,
        },
      },
    };
  }),
  on(AsyncValidationActions.trackWasAnalyzed, (state, { id }): WizardFeatureState => {
    if (state.value.tracklisting.every((track) => track.id !== id)) {
      return state;
    }

    return {
      ...state,
      asyncValidations: {
        ...state.asyncValidations,
        trackWasAnalyzed: { ...state.asyncValidations.trackWasAnalyzed, [id]: true },
      },
    };
  }),
  on(WizardSessionActions.startedLoadingConstraints, (state): WizardFeatureState => {
    return {
      ...state,
      constraints: {
        ...state.constraints,
        loading: true,
      },
    };
  }),
  on(WizardSessionActions.failedLoadingConstraints, (state): WizardFeatureState => {
    return {
      ...state,
      constraints: {
        ...initialState.constraints,
        error: true,
      },
    };
  }),
  on(WizardSessionActions.canceledLoadingConstraints, (state): WizardFeatureState => {
    return {
      ...state,
      constraints: initialState.constraints,
    };
  }),
  on(WizardSessionActions.finishedLoadingConstraints, (state, { type, ...constraints }): WizardFeatureState => {
    const newCountries = state.value.countrySelection.countries.filter((c) => constraints.allowedCountries.includes(c));

    const countriesChanged = newCountries.length !== state.value.countrySelection.countries.length;

    const newValue = countriesChanged
      ? { ...state.value, countrySelection: { ...state.value.countrySelection, countries: newCountries } }
      : state.value;

    return {
      ...state,
      value: newValue,
      constraints: {
        ...constraints,
        loading: false,
        error: false,
      },
    };
  }),
  on(WizardSessionActions.stepChanged, (state, { newStep }): WizardFeatureState => {
    const oldStep = state.value.stepProgress || WizardStep.SESSION_SELECT;

    if (newStep > oldStep) {
      return {
        ...state,
        value: {
          ...state.value,
          stepProgress: newStep,
        },
      };
    }

    return state;
  }),
  on(WizardSessionActions.initialLabelSelected, (state, { labelId }): WizardFeatureState => {
    // Initial label. Should not trigger a database save.
    return {
      ...state,
      value: {
        ...state.value,
        productData: { ...state.value.productData, label: labelId },
      },
    };
  }),
  on(WizardSessionActions.startedLoadingExistingTracks, (state, { ids }): WizardFeatureState => {
    const existingTrackData = { ...state.existingTrackData };

    ids.forEach((id) => {
      existingTrackData[id] = 'pending';
    });

    return {
      ...state,
      existingTrackData,
    };
  }),
  on(
    WizardSessionActions.finishedLoadingExistingTrack,
    (state, { id, title, titleVersion, isrc, displayArtist, ...rest }): WizardFeatureState => {
      const foundTrack = state.value.tracklisting.find((t): t is ExistingTrack => Boolean(t.existing) && t.id === id);

      if (
        foundTrack &&
        ((title && foundTrack.title !== title) ||
          (titleVersion && titleVersion !== foundTrack.titleVersion) ||
          (isrc && isrc !== foundTrack.isrc) ||
          (displayArtist && displayArtist !== foundTrack.displayArtist))
      ) {
        // Update saved track data as well.
        return {
          ...state,
          existingTrackData: { ...state.existingTrackData, [id]: rest },
          value: {
            ...state.value,
            tracklisting: state.value.tracklisting.map((t) => {
              if (!t.existing || t.id !== id) {
                return t;
              }

              const newT = { ...t };

              if (title) {
                newT.title = title;
              }
              if (titleVersion) {
                newT.titleVersion = titleVersion;
              }
              if (isrc) {
                newT.isrc = isrc;
              }
              if (displayArtist) {
                newT.displayArtist = displayArtist;
              }

              return newT;
            }),
          },
        };
      }

      return {
        ...state,
        existingTrackData: { ...state.existingTrackData, [id]: rest },
      };
    },
  ),
  on(WizardSessionActions.failedLoadingExistingTrack, (state, { id }): WizardFeatureState => {
    return {
      ...state,
      existingTrackData: { ...state.existingTrackData, [id]: 'error' },
    };
  }),
  on(WizardSessionActions.retryLoadingExistingTrack, (state, { id }): WizardFeatureState => {
    const { [id]: _, ...existingTrackData } = state.existingTrackData;

    return {
      ...state,
      existingTrackData,
    };
  }),
  on(UploadActions.start, (state, { sessionId, uploads }): WizardFeatureState => {
    if (state.value.id !== sessionId) {
      return state;
    }

    const newProgress: typeof state.uploadProgress = {};

    uploads.forEach((u) => {
      newProgress[u.id] = { error: undefined, progress: 0 };
    });

    return {
      ...state,
      uploadProgress: {
        ...state.uploadProgress,
        ...newProgress,
      },
    };
  }),
  on(UploadActions.done, (state, { sessionId, id, objectUri }): WizardFeatureState => {
    if (state.value.id !== sessionId) {
      return state;
    }

    const { [id]: removedProgress, ...rest } = state.uploadProgress;

    const progressUpdated = removedProgress === undefined ? state : { ...state, uploadProgress: rest };

    return updateUploadInfo(progressUpdated, id, (info) => ({ ...info, status: 'done', objectUri }));
  }),
  on(UploadActions.multiCreated, (state, { sessionId, id, uploadId, objectUri }): WizardFeatureState => {
    if (state.value.id !== sessionId) {
      return state;
    }

    return updateUploadInfo(state, id, () => ({
      uploadType: 'multi',
      status: 'running',
      uploadId,
      objectUri,
      finishedParts: [],
    }));
  }),
  on(UploadActions.partDone, (state, { sessionId, id, partNumber, etag }): WizardFeatureState => {
    if (state.value.id !== sessionId) {
      return state;
    }

    return updateUploadInfo(state, id, (info) => {
      if (info.uploadType !== 'multi' || info.status !== 'running') {
        const err = new Error('Invalid State');
        logError(err, { extra: { state, id, partNumber, etag, info } });

        return info;
      }

      return {
        ...info,
        finishedParts: [...info.finishedParts, { partNumber, etag }],
      };
    });
  }),
  on(UploadActions.allPartsDone, (state, { sessionId, id }): WizardFeatureState => {
    if (state.value.id !== sessionId) {
      return state;
    }

    return updateUploadInfo(state, id, (info) => {
      if (
        info.uploadType !== 'multi' ||
        info.status !== 'running' ||
        info.uploadId === undefined ||
        info.objectUri === undefined
      ) {
        const err = new Error('Invalid State');
        logError(err, { extra: { state, id } });

        return info;
      }

      return {
        ...info,
        status: 'waiting',
        uploadId: info.uploadId,
        objectUri: info.objectUri,
      };
    });
  }),
  on(UploadActions.progress, (state, { sessionId, id, progress }): WizardFeatureState => {
    if (state.value.id !== sessionId) {
      return state;
    }

    if (state.uploadProgress[id] === undefined) {
      return state;
    }

    return {
      ...state,
      uploadProgress: {
        ...state.uploadProgress,
        [id]: { ...state.uploadProgress[id], progress },
      },
    };
  }),
  on(UploadActions.error, (state, { sessionId, id, errorType }): WizardFeatureState => {
    if (state.value.id !== sessionId) {
      return state;
    }

    if (state.uploadProgress[id] === undefined) {
      return state;
    }

    return {
      ...state,
      uploadProgress: {
        ...state.uploadProgress,
        [id]: { ...state.uploadProgress[id], error: errorType },
      },
    };
  }),
  on(UploadActions.cancel, (state, { sessionId, id }): WizardFeatureState => {
    if (state.value.id !== sessionId) {
      return state;
    }

    const { [id]: removedProgress, ...rest } = state.uploadProgress;

    if (removedProgress === undefined) {
      return state;
    }

    return {
      ...state,
      uploadProgress: rest,
    };
  }),
);

export const wizardFeature = createFeature({
  name: 'product_wizard',
  reducer,
});

export type WizardStore = Store<{ [wizardFeature.name]: WizardFeatureState }>;
